/**
 * HUD - Heads-Up Display for game information
 * Shows score, height progress, and stability countdown
 * Styled with "Tactile Minimalism" aesthetic
 */

import Phaser from 'phaser';
import { GAME, COLORS } from '../config/gameConfig';

/**
 * Text style configuration for HUD elements
 */
const TEXT_STYLES = {
  score: {
    fontFamily: 'system-ui, -apple-system, sans-serif',
    fontSize: '32px',
    color: '#2D3436',
    fontStyle: 'bold',
    stroke: '#ffffff',
    strokeThickness: 3,
  },
  highScore: {
    fontFamily: 'system-ui, -apple-system, sans-serif',
    fontSize: '14px',
    color: '#636E72',
    stroke: '#ffffff',
    strokeThickness: 1,
  },
  pieces: {
    fontFamily: 'system-ui, -apple-system, sans-serif',
    fontSize: '13px',
    color: '#636E72',
    stroke: '#ffffff',
    strokeThickness: 1,
  },
  stability: {
    fontFamily: 'system-ui, -apple-system, sans-serif',
    fontSize: '72px',
    color: '#4CAF50',
    fontStyle: 'bold',
    stroke: '#ffffff',
    strokeThickness: 4,
  },
  popup: {
    fontFamily: 'system-ui, -apple-system, sans-serif',
    fontSize: '22px',
    color: '#4CAF50',
    fontStyle: 'bold',
    stroke: '#ffffff',
    strokeThickness: 3,
  },
} as const;

/**
 * HUD display for game information with polished styling
 */
export class HUD {
  private scene: Phaser.Scene;
  private scoreText: Phaser.GameObjects.Text;
  private highScoreText: Phaser.GameObjects.Text;
  private heightBar: Phaser.GameObjects.Graphics;
  private heightBarBg: Phaser.GameObjects.Graphics;
  private stabilityText: Phaser.GameObjects.Text;
  private piecesText: Phaser.GameObjects.Text;
  private lastScore: number = 0;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
    this.heightBarBg = this.createHeightBarBackground();
    this.heightBar = this.createHeightBar();
    this.scoreText = this.createScoreText();
    this.highScoreText = this.createHighScoreText();
    this.stabilityText = this.createStabilityText();
    this.piecesText = this.createPiecesText();
  }

  /**
   * Create score text display with polished style
   */
  private createScoreText(): Phaser.GameObjects.Text {
    const text = this.scene.add.text(GAME.WIDTH - 20, 80, '0', TEXT_STYLES.score);
    text.setOrigin(1, 0);
    text.setDepth(100);
    return text;
  }

  /**
   * Create high score text display
   */
  private createHighScoreText(): Phaser.GameObjects.Text {
    const text = this.scene.add.text(GAME.WIDTH - 20, 115, 'BEST: 0', TEXT_STYLES.highScore);
    text.setOrigin(1, 0);
    text.setDepth(100);
    return text;
  }

  /**
   * Create height progress bar background
   */
  private createHeightBarBackground(): Phaser.GameObjects.Graphics {
    const graphics = this.scene.add.graphics();
    graphics.setDepth(99);

    const barX = GAME.WIDTH - 18;
    const barWidth = 10;
    const barTop = 170;
    const barBottom = GAME.HEIGHT - 100;
    const barHeight = barBottom - barTop;

    // Soft shadow
    graphics.fillStyle(0x000000, 0.08);
    graphics.fillRoundedRect(barX - 1 + 2, barTop + 2, barWidth + 2, barHeight, 5);

    // Bar background with rounded corners
    graphics.fillStyle(0xd8dde4, 1);
    graphics.fillRoundedRect(barX - 1, barTop, barWidth + 2, barHeight, 5);

    // Inner border
    graphics.lineStyle(1, 0x000000, 0.05);
    graphics.strokeRoundedRect(barX - 1, barTop, barWidth + 2, barHeight, 5);

    return graphics;
  }

  /**
   * Create height progress bar
   */
  private createHeightBar(): Phaser.GameObjects.Graphics {
    const graphics = this.scene.add.graphics();
    graphics.setDepth(100);
    return graphics;
  }

  /**
   * Create stability countdown text
   */
  private createStabilityText(): Phaser.GameObjects.Text {
    const text = this.scene.add.text(GAME.WIDTH / 2, 150, '', TEXT_STYLES.stability);
    text.setOrigin(0.5);
    text.setDepth(100);
    text.setVisible(false);
    return text;
  }

  /**
   * Create pieces count text
   */
  private createPiecesText(): Phaser.GameObjects.Text {
    const text = this.scene.add.text(GAME.WIDTH - 20, 138, 'Pieces: 0', TEXT_STYLES.pieces);
    text.setOrigin(1, 0);
    text.setDepth(100);
    return text;
  }

  /**
   * Update score display with animation
   */
  updateScore(score: number): void {
    this.scoreText.setText(score.toString());

    // Pop animation on score increase
    if (score > this.lastScore) {
      this.scene.tweens.killTweensOf(this.scoreText);
      this.scoreText.setScale(1);

      this.scene.tweens.add({
        targets: this.scoreText,
        scaleX: 1.25,
        scaleY: 1.25,
        duration: 80,
        yoyo: true,
        ease: 'Back.easeOut',
      });
    }

    this.lastScore = score;
  }

  /**
   * Update high score display
   */
  updateHighScore(highScore: number): void {
    this.highScoreText.setText(`BEST: ${highScore}`);
  }

  /**
   * Update pieces count display
   */
  updatePiecesCount(count: number): void {
    this.piecesText.setText(`Pieces: ${count}`);
  }

  /**
   * Update height progress bar
   * @param currentHeight - Current highest point Y position
   * @param zenithY - Zenith line Y position
   * @param floorY - Floor Y position
   */
  updateHeightBar(currentHeight: number, zenithY: number, floorY: number): void {
    this.heightBar.clear();

    const barX = GAME.WIDTH - 17;
    const barWidth = 8;
    const barTop = 172;
    const barBottom = GAME.HEIGHT - 102;
    const barHeight = barBottom - barTop;

    // Calculate progress (0 to 1, where 1 = at zenith)
    const totalRange = floorY - zenithY;
    const currentProgress = floorY - currentHeight;
    const progress = Math.max(0, Math.min(1, currentProgress / totalRange));

    // Progress fill with rounded ends
    const fillHeight = barHeight * progress;

    if (fillHeight > 0) {
      // Gradient effect based on progress
      const progressColor = progress >= 1 ? COLORS.SUCCESS : 0x7c9885;

      this.heightBar.fillStyle(progressColor, 0.9);
      this.heightBar.fillRoundedRect(
        barX,
        barBottom - fillHeight,
        barWidth,
        fillHeight,
        4
      );

      // Subtle inner highlight
      this.heightBar.fillStyle(0xffffff, 0.2);
      this.heightBar.fillRoundedRect(
        barX + 1,
        barBottom - fillHeight + 2,
        3,
        Math.max(0, fillHeight - 4),
        2
      );
    }

    // Zenith marker with improved styling
    const zenithMarkerY = barTop;
    this.heightBar.fillStyle(0xff7043, 1);
    this.heightBar.fillRoundedRect(barX - 6, zenithMarkerY - 2, barWidth + 12, 4, 2);

    // Zenith marker shadow
    this.heightBar.fillStyle(0x000000, 0.1);
    this.heightBar.fillRoundedRect(barX - 6, zenithMarkerY + 2, barWidth + 12, 2, 1);
  }

  /**
   * Show stability countdown with animated styling
   * @param secondsRemaining - Seconds remaining until win
   */
  showStabilityCountdown(secondsRemaining: number): void {
    this.stabilityText.setVisible(true);
    this.stabilityText.setText(secondsRemaining.toFixed(1));

    // Color and size changes as time decreases
    if (secondsRemaining <= 1) {
      this.stabilityText.setColor('#4CAF50');
      this.stabilityText.setFontSize(80);
      this.stabilityText.setStroke('#ffffff', 5);
    } else if (secondsRemaining <= 2) {
      this.stabilityText.setColor('#8BC34A');
      this.stabilityText.setFontSize(76);
      this.stabilityText.setStroke('#ffffff', 4);
    } else {
      this.stabilityText.setColor('#9BA4B0');
      this.stabilityText.setFontSize(72);
      this.stabilityText.setStroke('#ffffff', 4);
    }

    // Pulse animation
    if (!this.scene.tweens.isTweening(this.stabilityText)) {
      this.scene.tweens.add({
        targets: this.stabilityText,
        scale: 1.05,
        duration: 200,
        yoyo: true,
        ease: 'Sine.easeInOut',
      });
    }
  }

  /**
   * Hide stability countdown
   */
  hideStabilityCountdown(): void {
    this.stabilityText.setVisible(false);
    this.scene.tweens.killTweensOf(this.stabilityText);
    this.stabilityText.setScale(1);
  }

  /**
   * Show score popup at position with improved styling
   */
  showScorePopup(x: number, y: number, points: number): void {
    const popup = this.scene.add.text(x, y - 20, `+${points}`, TEXT_STYLES.popup);
    popup.setOrigin(0.5);
    popup.setDepth(150);
    popup.setScale(0.5);
    popup.setAlpha(0);

    // Entrance animation
    this.scene.tweens.add({
      targets: popup,
      scale: 1,
      alpha: 1,
      duration: 150,
      ease: 'Back.easeOut',
    });

    // Float up and fade
    this.scene.tweens.add({
      targets: popup,
      y: y - 80,
      alpha: 0,
      duration: 900,
      delay: 150,
      ease: 'Quad.easeOut',
      onComplete: () => popup.destroy(),
    });
  }

  /**
   * Destroy all HUD elements
   */
  destroy(): void {
    this.scoreText.destroy();
    this.highScoreText.destroy();
    this.heightBar.destroy();
    this.heightBarBg.destroy();
    this.stabilityText.destroy();
    this.piecesText.destroy();
  }
}
